import { useState } from 'react';
import type { TFunction } from 'i18next';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  DataTable,
  RowActions,
  StatusBadge,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import type {
  AdminMemberSummary,
  MemberStatus,
} from '../hooks/useAdminUsersQuery';
import { UserStatusChangeDialog } from './UserStatusChangeDialog';
import { useAdminUserUnlockMutation } from '../hooks/useAdminUsersQuery';
import { memberStatusLabel, memberTypeLabel, nationalityLabel, userMallLabel } from '../lib/userLabels';
import { mobileDisplay } from '../lib/mobileDisplay';

interface UserTableProps {
  items: ReadonlyArray<AdminMemberSummary>;
  detailBasePath?: string;
}

type TFn = TFunction<'common', undefined>;

const STATUS_VARIANT: Record<MemberStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ACTIVE: 'success',
  DORMANT: 'warning',
  SUSPENDED: 'danger',
  WITHDRAWN: 'neutral',
  // R6 O — 기업회원 승인 게이트 상태(개인 회원목록에는 보통 노출되지 않으나 라벨/배지 안전).
  PENDING_APPROVAL: 'warning',
  REJECTED: 'danger',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ko-KR');
  } catch {
    return iso;
  }
};

const nextStatusOf = (status: MemberStatus): MemberStatus | null => {
  if (status === 'ACTIVE') return 'SUSPENDED';
  if (status === 'SUSPENDED') return 'ACTIVE';
  return null;
};

interface BuildColumnsArgs {
  t: TFn;
  detailBasePath?: string;
  onSetStatus: (userId: string, target: MemberStatus) => void;
  onUnlock?: (userId: string) => void;
  unlockPending?: boolean;
}

export const buildUserColumns = ({
  t,
  detailBasePath = '/users/members',
  onSetStatus,
  onUnlock,
  unlockPending = false,
}: BuildColumnsArgs): ReadonlyArray<DataTableColumn<AdminMemberSummary>> => [
  {
    // QA-396 — 로그인 ID 를 첫 칸에(상세에 들어가야만 보였다). 이름처럼 상세로 가는 링크.
    key: 'loginId',
    header: t('users.cols.loginId', '로그인 ID'),
    cell: (row) =>
      row.loginId ? (
        <Link
          to={`${detailBasePath}/${row.userId}`}
          className="text-[12px] underline-offset-2 hover:underline"
          style={{ color: 'var(--content-default)' }}
          data-testid="user-login-id"
        >
          {row.loginId}
        </Link>
      ) : (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>—</span>
      ),
    width: 120,
  },
  {
    key: 'name',
    header: t('users.cols.name'),
    cell: (row) => (
      <Link
        to={`${detailBasePath}/${row.userId}`}
        className="font-medium underline-offset-2 hover:underline"
        style={{ color: 'var(--content-strong)' }}
      >
        {row.name}
      </Link>
    ),
    width: 120,
  },
  {
    // 이름 바로 옆에 둔다 — 같은 아이디·이메일이 몰마다 별개 계정으로 존재할 수 있어,
    // 동명이인 행 중 어느 몰의 회원인지 즉시 구분되지 않으면 오조작 위험이 있다.
    key: 'mallCode',
    header: t('users.cols.mallCode', '국가몰'),
    cell: (row) => (
      <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
        {userMallLabel(t, row.mallCode)}
      </span>
    ),
    width: 84,
  },
  {
    key: 'email',
    header: t('users.cols.email'),
    cell: (row) => (
      <span
        className="block max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap text-[12px]"
        style={{ color: 'var(--content-default)' }}
        title={row.email}
      >
        {row.email}
      </span>
    ),
  },
  {
    key: 'mobileNumber',
    header: t('users.cols.mobileNumber', '휴대전화'),
    cell: (row) => (
      <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-default)' }}>
        {mobileDisplay(row.mobileNumber) || '—'}
      </span>
    ),
    width: 132,
  },
  {
    key: 'memberType',
    header: t('users.cols.memberType'),
    cell: (row) => (
      <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
        {memberTypeLabel(t, row.memberType)}
      </span>
    ),
    width: 84,
  },
  {
    key: 'nationality',
    header: t('users.cols.nationality'),
    cell: (row) => (
      <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
        {nationalityLabel(t, row.nationality)}
      </span>
    ),
    width: 80,
  },
  {
    key: 'status',
    header: t('users.cols.status'),
    cell: (row) => (
      <span className="inline-flex items-center gap-1.5">
        <StatusBadge label={memberStatusLabel(t, row.status, row.memberType)} variant={STATUS_VARIANT[row.status] ?? 'neutral'} />
        {row.locked ? (
          <StatusBadge label={t('users.locked.badge')} variant="danger" size="sm" />
        ) : null}
      </span>
    ),
    width: 124,
  },
  {
    key: 'lastLoginAt',
    header: t('users.cols.lastLoginAt'),
    cell: (row) => (
      <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
        {formatDate(row.lastLoginAt)}
      </span>
    ),
    width: 112,
  },
  {
    key: 'createdAt',
    header: t('users.cols.createdAt'),
    cell: (row) => (
      <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
        {formatDate(row.createdAt)}
      </span>
    ),
    width: 112,
  },
  {
    key: 'actions',
    header: '',
    align: 'right',
    width: 56,
    cell: (row) => {
      const next = nextStatusOf(row.status);
      const actions: RowActionItem[] = [
        {
          key: 'detail',
          label: t('users.actions.detail'),
          onClick: () => {
            window.location.href = `${detailBasePath}/${row.userId}`;
          },
        },
      ];
      if (next) {
        actions.push({
          key: `set-${next}`,
          label: t(`users.actions.setStatus.${next}`),
          variant: next === 'SUSPENDED' ? 'danger' : 'default',
          separatorBefore: true,
          onClick: () => onSetStatus(row.userId, next),
        });
      }
      if (row.locked && onUnlock) {
        actions.push({
          key: 'unlock',
          label: t('users.actions.unlock'),
          disabled: unlockPending,
          onClick: () => onUnlock(row.userId),
        });
      }
      return (
        <RowActions
          triggerLabel={t('users.actions.menuAria', '회원 액션')}
          actions={actions}
        />
      );
    },
  },
];

export const UserTable = ({ items, detailBasePath = '/users/members' }: UserTableProps) => {
  const { t } = useTranslation('common');
  const unlockMutation = useAdminUserUnlockMutation();
  const [statusDialog, setStatusDialog] = useState<{
    userId: string;
    target: MemberStatus;
  } | null>(null);

  const columns = buildUserColumns({
    t,
    detailBasePath,
    onSetStatus: (userId, target) => setStatusDialog({ userId, target }),
    onUnlock: (userId) => unlockMutation.mutate(userId),
    unlockPending: unlockMutation.isPending,
  });

  return (
    <>
      <DataTable
        columns={[...columns]}
        rows={[...items]}
        rowKey={(row) => row.userId}
        stickyHeader
        mobileCardCollapse
        emptyState={t('users.list.empty')}
        ariaLabel={t('users.list.title')}
      />

      {statusDialog ? (
        <UserStatusChangeDialog
          open
          onOpenChange={(o) => {
            if (!o) setStatusDialog(null);
          }}
          userId={statusDialog.userId}
          targetStatus={statusDialog.target}
          onSuccess={() => setStatusDialog(null)}
        />
      ) : null}
    </>
  );
};
